<template>
    <transition>
    <div class="container-fluid page-content">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
            <div>
                <h4 class="page-title text-slate-800 fw-bold mb-1">Warehouse</h4>
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                        <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Settings</a></li>
                        <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Warehouse List</li>
                    </ol>
                </nav>
            </div>
            <div>
                <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal" v-if="permission['warehouse-create']">
                    <i class="fas fa-plus"></i> Add New Warehouse
                </button>
            </div>
        </div>

        <!-- Modern Filter Bar -->
        <div class="card border-0 shadow-sm filter-card">
            <div class="card-body p-3">
                <div class="row align-items-center g-2">
                    <div class="col-md-6 col-sm-6 d-flex align-items-center gap-2">
                        <span class="text-muted text-nowrap">Show</span>
                        <select class="form-select-custom length-select" v-model="tableData.length" @change="fetchIndexData()">
                            <option value="10" selected="selected">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option :value="pagination.total">All</option>
                        </select>
                        <span class="text-muted text-nowrap">entries</span>

                        <!-- Company Filter -->
                        <span class="text-muted text-nowrap ms-2">Company</span>
                        <select class="form-select-custom" style="width: auto; min-width: 150px;" v-model="tableData.company_id" @change="fetchIndexData()">
                            <option value="">All Companies</option>
                            <option v-for="(company, index) in companies" :value="company.id" :key="index">
                                {{ company.name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-6 col-sm-6 d-flex justify-content-sm-end">
                        <div class="position-relative search-container">
                            <i class="fas fa-search search-icon text-muted"></i>
                            <input type="text" class="form-control-custom search-input" v-model="tableData.search" placeholder="Search warehouses..." @input="fetchIndexData()">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm table-card">
            <div class="card-body p-0">
                <div class="table-responsive">
                    <table class="table table-premium mb-0" v-if="!loading && items.length > 0">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 5%;">SL</th>
                                <th class="text-start" style="width: 15%;">Company</th>
                                <th class="text-start" style="width: 18%;">Warehouse Name</th>
                                <th class="text-start" style="width: 20%;">Contact Person Name</th>
                                <th class="text-start" style="width: 18%;">Warehouse Number</th>
                                <th class="text-start" style="width: 16%;">Address</th>
                                <th class="text-center" style="width: 8%;">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in items" :key="i">
                                <td class="text-center text-muted fw-semibold">{{ (pagination && pagination.currentPage ? (pagination.currentPage - 1) * tableData.length : 0) + i + 1 }}</td>
                                <td class="text-start">{{ item.company_name }}</td>
                                <td class="text-start">
                                    <span class="fw-bold text-slate-800">{{ item.name }}</span>
                                </td>
                                <td class="text-start">{{ item.contact_person_name }}</td>
                                <td class="text-start">{{ item.warehouse_number }}</td>
                                <td class="text-start">{{ item.address }}</td>
                                <td class="text-center">
                                    <div class="dropdown">
                                        <button class="btn btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">
                                            <li v-if="permission['warehouse-edit']">
                                                <a class="dropdown-item text-primary" href="javascript:void(0);" @click="edit(item)">
                                                    <i class="fas fa-edit me-2"></i>Edit
                                                </a>
                                            </li>
                                            <li v-if="permission['warehouse-edit'] && permission['warehouse-delete']"><hr class="dropdown-divider"></li>
                                            <li v-if="permission['warehouse-delete']">
                                                <a class="dropdown-item text-danger" href="javascript:void(0);" @click="deleteItem(item)">
                                                    <i class="fas fa-trash-alt me-2"></i>Remove
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Empty State -->
                    <div v-if="!loading && items.length === 0" class="text-center py-5">
                        <img src="../../assets/image/loading.gif" class="mb-3 opacity-25" style="width: 80px; filter: grayscale(1);" />
                        <h5 class="text-muted fw-semibold">No Data Found</h5>
                        <p class="text-muted mb-0 small">Try adjusting your filters or search keywords.</p>
                    </div>

                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-premium mb-3"></div>
                        <h5 class="text-muted fw-semibold">Loading Data</h5>
                        <p class="text-muted mb-0 small">Please wait a moment...</p>
                    </div>
                </div>

                <!-- Modern Pagination Footer -->
                <div class="card-footer border-0 bg-white py-3 px-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3" v-if="pagination && items.length > 0">
                    <div class="text-muted small">
                        Showing <span class="fw-semibold text-slate-800">{{ pagination.from }}</span> to <span class="fw-semibold text-slate-800">{{ pagination.to }}</span> of <span class="fw-semibold text-slate-800">{{ pagination.total }}</span> entries
                    </div>
                    <Pagination 
                        :pagination="pagination"  
                        :language="lang"
                        @onChangePage="setPage" > 
                    </Pagination>
                </div>
            </div>
        </div>

        <Modal @close="toggleModal()" :modalActive="modalActive">
            <div class="modal-content border-0 shadow-lg modal-premium">
                <div class="modal-header">
                    <h4 class="fw-bold m-0">{{ editMode ? 'Update Warehouse' : 'Create New Warehouse' }}</h4>
                    <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                </div>
                <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-12">
                                <div class="row">
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Warehouse Name *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.name}" @keypress="onkeyPress('name')" v-model="form.name" id="name" placeholder="Warehouse Name" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{errors.name[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company Name *</label>
                                            <select class="form-select-custom" :class="{'is-invalid': errors.company_id}" v-model="form.company_id" @change="onkeyPress('company_id')">
                                                <option value="">Select company</option>
                                                <option v-for="(company, index) in companies" :value="company.id" :key="index">
                                                    {{company.name}}
                                                </option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.company_id">
                                                {{errors.company_id[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="contact_person_name" class="form-label-custom text-slate-700 fw-semibold mb-2">Contact person name *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.contact_person_name}" @keypress="onkeyPress('contact_person_name')" v-model="form.contact_person_name" id="contact_person_name" placeholder="Contact person name">
                                            <div class="invalid-feedback" v-if="errors.contact_person_name">
                                                {{errors.contact_person_name[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="warehouse_number" class="form-label-custom text-slate-700 fw-semibold mb-2">Warehouse number *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.warehouse_number}" @keypress="onkeyPress('warehouse_number')" v-model="form.warehouse_number" id="warehouse_number" placeholder="Warehouse number">
                                            <div class="invalid-feedback" v-if="errors.warehouse_number">
                                                {{errors.warehouse_number[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="address" class="form-label-custom text-slate-700 fw-semibold mb-2">Address *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.address}" @keypress="onkeyPress('address')" v-model="form.address" id="address" placeholder="Address" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.address">
                                                {{errors.address[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group col-md-6">
                                        <div class="mb-3">
                                            <label for="status" class="form-label-custom text-slate-700 fw-semibold mb-2">Status *</label>
                                            <select class="form-select-custom" :class="{'is-invalid': errors.status}" v-model="form.status" @change="onkeyPress('status')">
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.status">
                                                {{errors.status[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pt-0">
                        <button type="button" class="btn btn-outline-premium me-2" @click="toggleModal()">Cancel</button>
                        <button type="submit" class="btn btn-premium" :disabled="disabled">
                            <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ editMode ? 'Update Warehouse' : 'Create Warehouse' }}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    </div>
    </transition>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { useRequisitionProductStore } from '@/stores/requisitionProduct';


import Modal from "./../helper/Modal";
import Pagination from '@/components/Pagination.vue';
import { ref, onMounted } from "vue";
import Form from 'vform'
import axios from 'axios'; 
export default {
    name: 'Warehouse',
    props: {
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            base_url: this.apiUrl,
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            loading: true,
            errors: {},
            logoPreview:'',
            btn:'Create',
            items: [],
            outlets: [],
            districts: [],
            areas: [],
            companies: [],
            form: new Form({
                id: '',
                name: '',
                company_id:'', 
                contact_person_name: '',
                warehouse_number: '', 
                address: '',
                status: 1,
            }),
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                company_id: '',
                column: 0,
                dir: 'desc',
                sortKey: 'name', 
            }, 
            lang: {
                lengthMenu: this.$props.language?.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language?.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language?.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language?.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            }
        };
    }, 
    components: {
        Modal,
        Pagination,
    }, 
    methods: { 
        toggleModal: function() {
            this.modalActive = !this.modalActive;   
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset();
        },
        fetchIndexData(url = this.base_url+'/warehouses') {  
            this.tableData.draw++;
            this.loading = true;
            axios.get(url, {params: this.tableData, headers: this.headerparams})
            .then((res) => {
                this.items = res.data.data; 
                this.configPagination(res.data.meta);
            })
            .catch((err) => { 
                this.$toast.error(err.response?.data?.message || err.message); 
            }).finally((ress) => {
                this.loading = false; 
            });
        }, 
        fetchCompany() { 
            axios.get(this.base_url+'/companies', this.headerjson)
            .then((res) => {
                this.companies = res.data.data; 
            })
            .catch((err) => { 
                this.$toast.error(err.response.data.message); 
            });
        },  
        edit: function(item) { 
            this.btn='Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item);  
        },
        submitForm: function(e) {  
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('name', this.form.name);            
            formData.append('company_id', this.form.company_id);
            formData.append('contact_person_name', this.form.contact_person_name);
            formData.append('warehouse_number', this.form.warehouse_number);
            formData.append('address', this.form.address); 
            formData.append('status', this.form.status);   
            if(this.editMode){
                formData.append('_method', 'put'); 
                var postEvent = axios.post(this.base_url+'/warehouses/'+this.form.id, formData, this.headers);
            }else{ 
                this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                var postEvent = axios.post(this.base_url+'/warehouses', formData, this.headers);
            }           

            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal();
                    this.fetchIndexData();
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                }
            }).catch(err => { 
                this.isSubmit = false; 
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }
            });
        },
        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.base_url+'/warehouses/'+item.id, this.headerjson)
                    .then(res => {
                        if(res.status == 200){  
                            this.fetchIndexData();
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        }
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message); 
                    }) 
                }
            }); 
        }, 
        configPagination(data){
            if (!data) return;
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },
        setPage(data){  
            this.fetchIndexData(data.url); 
        },
    },
    created() {
        this.fetchIndexData(); 
        this.fetchCompany();
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed:{
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
    }
}
</script>